import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { HiExclamation, HiShieldCheck, HiCode, HiArrowLeft, HiInformationCircle, HiDownload, HiClock, HiChip, HiServer } from 'react-icons/hi';
import { Link } from 'react-router-dom';
import BackgroundEffects from './BackgroundEffects';

const XboxExploit = () => {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check if user has already seen the loading screen
    const hasSeenLoading = localStorage.getItem('xboxExploitLoadingSeen');
    
    if (hasSeenLoading) {
      // User has seen it before, skip loading screen
      setIsLoading(false);
      return;
    }
    
    // Mark that user has seen the loading screen
    localStorage.setItem('xboxExploitLoadingSeen', 'true');
    
    // Create a hidden image element to track GIF loading
    const img = new Image();
    img.src = '/images/bue.gif';
    
    let gifTimer;
    
    // Request fullscreen when loading starts
    const requestFullscreen = () => {
      if (document.documentElement.requestFullscreen) {
        document.documentElement.requestFullscreen();
      } else if (document.documentElement.webkitRequestFullscreen) {
        document.documentElement.webkitRequestFullscreen();
      } else if (document.documentElement.msRequestFullscreen) {
        document.documentElement.msRequestFullscreen();
      }
    };
    
    // Try to enter fullscreen mode
    try {
      requestFullscreen();
    } catch (error) {
      console.log('Fullscreen request failed (this is normal)');
    }
    
    img.onload = () => {
      // Estimate GIF duration based on file size and typical frame rates
      // This is an approximation since we can't get exact GIF duration
      // Most security demo GIFs are 3-6 seconds
      const estimatedDuration = 7000; // 7 seconds
      
      gifTimer = setTimeout(() => {
        // Exit fullscreen before showing content
        try {
          if (document.exitFullscreen) {
            document.exitFullscreen();
          } else if (document.webkitExitFullscreen) {
            document.webkitExitFullscreen();
          } else if (document.msExitFullscreen) {
            document.msExitFullscreen();
          }
        } catch (error) {
          console.log('Fullscreen exit failed (this is normal)');
        }
        setIsLoading(false);
      }, estimatedDuration);
    };
    
    // Fallback timer in case image fails to load
    const fallbackTimer = setTimeout(() => {
      // Exit fullscreen before showing content
      try {
        if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        } else if (document.msExitFullscreen) {
          document.msExitFullscreen();
        }
      } catch (error) {
        console.log('Fullscreen exit failed (this is normal)');
      }
      setIsLoading(false);
    }, 8000); // 8 seconds fallback
    
    return () => {
      if (gifTimer) clearTimeout(gifTimer);
      clearTimeout(fallbackTimer);
    };
  }, []);

  if (isLoading) {
    return (
      <div 
        className="fixed inset-0 w-screen h-screen bg-black z-50 overflow-hidden"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          zIndex: 9999,
          cursor: 'none'
        }}
      >
        <img 
          src="/images/bue.gif"
          alt="Loading..."
          className="w-full h-full object-cover"
          style={{
            width: '100vw',
            height: '100vh',
            objectFit: 'cover',
            pointerEvents: 'none'
          }}
        />
        {/* Disable all interactions */}
        <div 
          className="absolute inset-0 bg-transparent"
          style={{ pointerEvents: 'none' }}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen px-6 pb-20 relative">
      {/* Background Effects */}
      <BackgroundEffects />
      
      <div className="max-w-6xl mx-auto pt-20 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Back Button */}
          <div className="mb-6">
            <Link to="/" className="inline-flex items-center text-accent-green hover:text-accent-green/80 transition-colors">
              <HiArrowLeft className="mr-2" />
              Back to Home
            </Link>
          </div>
          
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-2 glass-morphism px-6 py-3 rounded-full mb-6">
              <HiExclamation className="text-red-500" />
              <span className="font-jetbrains text-red-500 tracking-wider text-sm">
                SECURITY RESEARCH
              </span>
            </div>
            <h1 className="text-display-2 font-space-grotesk font-bold mb-4">
              <span className="text-white">Xbox 360 </span>
              <span className="gradient-text-red">BadUpdate v1.2</span>
            </h1>
            <h2 className="text-2xl font-space-grotesk font-bold gradient-text-green mb-6">
              Hypervisor Exploit Analysis
            </h2>
            <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
              A comprehensive analysis of the BadUpdate v1.2 software-only hypervisor exploit for Xbox 360, 
              featuring dramatic improvements in Stage 3 speed and success rate.
            </p>
          </div>

          {/* Downloads Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="glass-morphism-strong rounded-2xl p-8 mb-8"
          >
            <h3 className="text-2xl font-space-grotesk font-bold gradient-text-green mb-6 flex items-center">
              <HiDownload className="mr-3" />
              Downloads & Tools
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <a 
                href="https://github.com/grimdoomer/Xbox360BadUpdate/releases/download/v1.2/Xbox360BadUpdate-Retail-USB-v1.2.zip"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 px-6 py-4 rounded-xl bg-white/10 hover:bg-accent-green/80 hover:text-white text-accent-green font-semibold text-lg shadow transition-colors duration-200 backdrop-blur-md border border-accent-green/20"
              >
                <HiDownload className="w-7 h-7" />
                Xbox360BadUpdate v1.2 (Retail USB)
              </a>
                              <a 
                href="https://github.com/grimdoomer/Xbox360BadUpdate/releases/download/v1.2/Tools.zip"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 px-6 py-4 rounded-xl bg-white/10 hover:bg-accent-green/80 hover:text-white text-accent-green font-semibold text-lg shadow transition-colors duration-200 backdrop-blur-md border border-accent-green/20"
              >
                <HiDownload className="w-7 h-7" />
                XePatcher Tools v1.2
              </a>
            </div>
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Overview */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="glass-morphism-strong rounded-2xl p-8"
              >
                <h3 className="text-2xl font-space-grotesk font-bold gradient-text-red mb-6">
                  Overview
                </h3>
                <p className="text-gray-300 mb-6 leading-relaxed">
                  BadUpdate v1.2 is a software-only hypervisor exploit for the Xbox 360 that allows running unsigned code 
                  on even the latest dashboard (version 17559) with nothing more than a USB flash drive and a compatible game. 
                  The exploit subverts the secure boot/update process to overwrite part of the hypervisor in RAM, gaining 
                  full hypervisor-mode code execution.
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="flex items-center space-x-3">
                    <HiChip className="text-red-500 text-lg" />
                    <span className="text-gray-300">Software-only exploit</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <HiServer className="text-red-500 text-lg" />
                    <span className="text-gray-300">Hypervisor bypass</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <HiClock className="text-red-500 text-lg" />
                    <span className="text-gray-300">~80% success rate</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <HiCode className="text-red-500 text-lg" />
                    <span className="text-gray-300">~15s execution time</span>
                  </div>
                </div>
              </motion.div>

              {/* v1.2 Improvements */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="glass-morphism-strong rounded-2xl p-8"
              >
                <h3 className="text-2xl font-space-grotesk font-bold gradient-text-blue mb-6">
                  v1.2 Major Improvements
                </h3>
                <div className="space-y-6">
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-3">Stage 3 Performance Boost</h4>
                    <p className="text-gray-300 leading-relaxed">
                      Dramatically improved Stage 3 speed and success rate through advanced optimization techniques:
                    </p>
                    <ul className="list-disc list-inside text-gray-300 mt-2 space-y-1">
                      <li>Moved CIPHER_TEXT_DATA structure to cachable heap allocation</li>
                      <li>Implemented assembler code for ciphertext overwriter loop</li>
                      <li>Added "nop loop" timing compensation for earlier block hits</li>
                      <li>Precomputed ciphertext + replacement pairs for all possible whitening values</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-3">Advanced Lookup Strategy</h4>
                    <p className="text-gray-300 leading-relaxed">
                      New "pseudo-hashtable" approach using carefully chosen ciphertext bits for optimal cache performance:
                    </p>
                    <ul className="list-disc list-inside text-gray-300 mt-2 space-y-1">
                      <li>Single load lookup cost that always hits L2 cache</li>
                      <li>Optimized table size to fit in CPU cache</li>
                      <li>Nearly complete coverage of all ciphertext + replacement pairs</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-3">Success Metrics</h4>
                    <p className="text-gray-300 leading-relaxed">
                      v1.2 achieves approximately 80% success rate with each run from Stage 3 onward taking only ~15 seconds, 
                      representing a significant improvement over previous versions.
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Technical Analysis */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="glass-morphism-strong rounded-2xl p-8"
              >
                <h3 className="text-2xl font-space-grotesk font-bold gradient-text-blue mb-6">
                  Technical Analysis
                </h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-2">Exploit Stages</h4>
                    <ol className="list-decimal list-inside text-gray-300 text-sm space-y-2">
                      <li><strong>Game Payload (Trigger):</strong> Exploit vulnerability in Tony Hawk's American Wasteland or Rock Band Blitz save files</li>
                      <li><strong>Kernel-Mode Execution:</strong> Custom ROP chain with bootanim.xex execution and whitening collision detection</li>
                      <li><strong>Hypervisor Overwrite:</strong> Two-thread attack on bootloader update routine with LZX decoder manipulation</li>
                      <li><strong>Hypervisor Patching:</strong> Memory cleanup and RSA signature check bypass</li>
                    </ol>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-2">Race Condition Exploitation</h4>
                    <p className="text-gray-300 text-sm">
                      The exploit uses a race condition in the bootloader update path, turning an authenticated update packet 
                      into a write-what-you-want primitive under hypervisor context.
                    </p>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-2">Memory Manipulation</h4>
                    <p className="text-gray-300 text-sm">
                      LZX decoder context manipulation causes decompressed update blocks to overwrite hypervisor code memory, 
                      followed by syscall table patching for arbitrary code execution.
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Requirements & Compatibility */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="glass-morphism-strong rounded-2xl p-8"
              >
                <h3 className="text-2xl font-space-grotesk font-bold gradient-text-green mb-6">
                  Requirements & Compatibility
                </h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-3">Hardware Requirements</h4>
                    <ul className="space-y-2 text-sm text-gray-300">
                      <li>• Xbox 360 (any hardware revision)</li>
                      <li>• USB flash drive (FAT32 formatted)</li>
                      <li>• Compatible game disc (Tony Hawk's or Rock Band Blitz)</li>
                      <li>• No hardware modifications required</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-3">Software Requirements</h4>
                    <ul className="space-y-2 text-sm text-gray-300">
                      <li>• Dashboard version 17559 (retail)</li>
                      <li>• BadUpdate v1.2 exploit package</li>
                      <li>• Homebrew executable (default.xex)</li>
                      <li>• Offline operation recommended</li>
                    </ul>
                  </div>
                </div>
              </motion.div>

              {/* Usage Instructions */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="glass-morphism-strong rounded-2xl p-8"
              >
                <h3 className="text-2xl font-space-grotesk font-bold gradient-text-green mb-6">
                  Usage Instructions
                </h3>
                <ol className="list-decimal list-inside text-gray-300 space-y-3">
                  <li>Prepare USB drive as FAT32 and extract the BadUpdate v1.2 package</li>
                  <li>Place the game-specific folder (Tony Hawk's or Rock Band Blitz) on USB root</li>
                  <li>Add your homebrew executable as BadUpdatePayload\default.xex</li>
                  <li>Insert USB into Xbox 360 and power on with game disc</li>
                  <li>Load the provided hacked save file to trigger the exploit</li>
                  <li>Wait for solid green ring lights indicating success (~15 seconds)</li>
                  <li>If unsuccessful, reboot and retry (80% success rate)</li>
                </ol>
              </motion.div>

              {/* Security Implications */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="glass-morphism-strong rounded-2xl p-8"
              >
                <h3 className="text-2xl font-space-grotesk font-bold gradient-text-green mb-6">
                  Security Implications
                </h3>
                <div className="space-y-6">
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-3">Gaming Console Security</h4>
                    <p className="text-gray-300 leading-relaxed">
                      This research demonstrates critical vulnerabilities in gaming console security architectures 
                      and the importance of secure update mechanisms.
                    </p>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-3">Industry Impact</h4>
                    <p className="text-gray-300 leading-relaxed">
                      The BadUpdate exploit has reignited the Xbox 360 modding scene and influenced security 
                      practices in the gaming industry.
                    </p>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-3">Research Value</h4>
                    <p className="text-gray-300 leading-relaxed">
                      Contributes to understanding embedded system security, hypervisor vulnerabilities, 
                      and the challenges of securing consumer electronics devices.
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* References */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className="glass-morphism-strong rounded-2xl p-8"
              >
                <h3 className="text-2xl font-space-grotesk font-bold gradient-text-green mb-6">
                  References & Resources
                </h3>
                <div className="space-y-3">
                  <a 
                    href="https://github.com/grimdoomer"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-accent-green hover:text-accent-green/80 transition-colors"
                  >
                    • Ryan "GrimDoomer" Miceli's GitHub
                  </a>
                  <a 
                    href="https://www.youtube.com/shorts/lNw4zD8G_6g"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-accent-green hover:text-accent-green/80 transition-colors"
                  >
                    • Demo Video (YouTube)
                  </a>
                  <a 
                    href="https://www.copetti.org/writings/consoles/xbox-360/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-accent-green hover:text-accent-green/80 transition-colors"
                  >
                    • Xbox 360 Architecture Analysis
                  </a>
                </div>
              </motion.div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Version Info */}
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="glass-morphism-strong rounded-2xl p-6"
              >
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <HiCode className="text-2xl text-green-500" />
                  </div>
                  <h3 className="text-xl font-space-grotesk font-bold text-white mb-2">
                    v1.2 Release
                  </h3>
                  <p className="text-gray-300 text-sm">
                    Latest version with major improvements
                  </p>
                </div>
              </motion.div>

              {/* Performance Metrics */}
              <div className="glass-morphism-strong rounded-2xl p-6">
                <h4 className="text-lg font-space-grotesk font-bold text-white mb-4">
                  Performance Metrics
                </h4>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Success Rate:</span>
                    <span className="text-green-500">~80%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Execution Time:</span>
                    <span className="text-white">~15s</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Cache Hit Rate:</span>
                    <span className="text-white">L2 Optimized</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Memory Usage:</span>
                    <span className="text-white">Heap Allocated</span>
                  </div>
                </div>
              </div>

              {/* Research Details */}
              <div className="glass-morphism-strong rounded-2xl p-6">
                <h4 className="text-lg font-space-grotesk font-bold text-white mb-4">
                  Research Details
                </h4>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Severity:</span>
                    <span className="text-red-500">Critical</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Category:</span>
                    <span className="text-white">Hypervisor Exploit</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Platform:</span>
                    <span className="text-white">Xbox 360</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Version:</span>
                    <span className="text-white">v1.2</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Year:</span>
                    <span className="text-white">2025</span>
                  </div>
                </div>
              </div>

              {/* Disclaimer */}
              <div className="glass-morphism-strong rounded-2xl p-6">
                <h4 className="text-lg font-space-grotesk font-bold text-white mb-4">
                  Research Disclaimer
                </h4>
                <p className="text-xs text-gray-400 leading-relaxed">
                  This research is conducted for educational and security improvement purposes only. 
                  All findings have been responsibly disclosed. This research contributes to improving 
                  security practices in the gaming industry and understanding embedded system vulnerabilities.
                </p>
              </div>

              {/* Back to Home */}
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="glass-morphism-strong rounded-2xl p-6"
              >
                <Link to="/" className="block text-center">
                  <span className="text-accent-green hover:text-accent-green/80 transition-colors flex items-center justify-center">
                    <HiArrowLeft className="mr-2" />
                    Back to Home
                  </span>
                </Link>
              </motion.div>
            </div>
          </div>
          
          {/* GIF at Bottom */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="mt-16 text-center"
          >
            <div className="glass-morphism-strong rounded-2xl p-6 max-w-2xl mx-auto">
              <img 
                src="/images/bue.gif"
                alt="Xbox 360 BadUpdate v1.2 Exploit Demo"
                className="w-full h-auto rounded-xl shadow-lg"
              />
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default XboxExploit; 